import axios from 'axios';
import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { 
  Clock, MapPin, Play, Square, AlertTriangle, CheckCircle, 
  User, Calendar, Timer, Coffee, Building, ChevronRight,
  Navigation, RefreshCw, History, Search, Download, BarChart2,
  WifiOff, Wifi, CloudUpload, Bell, CheckCheck, X
} from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";

// ═══ OFFLINE UTILS ═══════════════════════════════════════════════
const OFFLINE_QUEUE_KEY = 'prqc_offline_punches_queue';
const OFFLINE_CURRENT_PUNCH_KEY = 'prqc_offline_current_punch';
const OFFLINE_BONS_CACHE_KEY = 'prqc_offline_bons_cache';
const OFFLINE_EMP_CACHE_KEY = 'prqc_offline_emp_cache';

const getOfflineQueue = () => { try { return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]'); } catch (e) { console.error('Erreur lecture queue hors-ligne:', e); return []; } };
const saveOfflineQueue = (q) => localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(q));
const getOfflineCurrentPunch = () => { try { return JSON.parse(localStorage.getItem(OFFLINE_CURRENT_PUNCH_KEY) || 'null'); } catch (e) { console.error('Erreur lecture punch hors-ligne:', e); return null; } };
const saveOfflineCurrentPunch = (p) => localStorage.setItem(OFFLINE_CURRENT_PUNCH_KEY, JSON.stringify(p));
const clearOfflineCurrentPunch = () => localStorage.removeItem(OFFLINE_CURRENT_PUNCH_KEY);
const cacheBons = (bons) => { try { localStorage.setItem(OFFLINE_BONS_CACHE_KEY, JSON.stringify(bons)); } catch (e) { console.error('Erreur cache bons:', e); } };
const getCachedBons = () => { try { return JSON.parse(localStorage.getItem(OFFLINE_BONS_CACHE_KEY) || '[]'); } catch (e) { console.error('Erreur lecture cache bons:', e); return []; } };
const cacheEmployees = (emps) => { try { localStorage.setItem(OFFLINE_EMP_CACHE_KEY, JSON.stringify(emps)); } catch (e) { console.error('Erreur cache employés:', e); } };
const getCachedEmployees = () => { try { return JSON.parse(localStorage.getItem(OFFLINE_EMP_CACHE_KEY) || '[]'); } catch (e) { console.error('Erreur lecture cache employés:', e); return []; } };

const TERRAIN_ROLES = ['chef_equipe', 'peintre', 'sous_traitant'];

export function Pointage() {
  const { user } = useAuth();
  // Tous les rôles utilisent Mon Pointage.
  // La différence se fait à l'intérieur du composant (terrain vs admin).
  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Clock className="w-5 h-5 text-blue-600" />
        <h1 className="text-lg font-bold text-gray-900">Mon Pointage</h1>
      </div>
      <PointageTerrain user={user} />
    </div>
  );
}

// ── Wrapper pour l'interface manager (ancienne interface) ────────
export function PointageManager() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [currentPunch, setCurrentPunch] = useState(null);
  const [punchHistory, setPunchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  
  // Mode hors-ligne
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingPunches, setPendingPunches] = useState(getOfflineQueue);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncInProgress = useRef(false);

  // Géolocation
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  // Contract selection
  const [selectedContract, setSelectedContract] = useState(null);
  const [showPunchInModal, setShowPunchInModal] = useState(false);
  const [showPunchOutModal, setShowPunchOutModal] = useState(false);
  const [pauseHours, setPauseHours] = useState(0.5);
  
  // Timer
  const [elapsedTime, setElapsedTime] = useState(null);

  // Charger les données initiales
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (!isOnline) {
        // Mode hors-ligne : utiliser le cache local
        const cachedEmps = getCachedEmployees();
        const cachedBons = getCachedBons();
        setEmployees(cachedEmps);
        setContracts(cachedBons);
        if (cachedEmps.length > 0) setSelectedEmployee(cachedEmps[0]);
        const offlinePunch = getOfflineCurrentPunch();
        if (offlinePunch) setCurrentPunch(offlinePunch);
        return;
      }
      const [empResponse] = await Promise.all([
        api.getEmployees({ actif: true }),
      ]);
      const emps = empResponse.employees || [];
      setEmployees(emps);
      cacheEmployees(emps);

      // Charger les bons de travail pour la sélection de contrat
      try {
        const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
        const bonsRes = await axios.get(`${API_URL}/api/crm/bons-travail`);
        if (bonsRes.status === 200) {
          const bonsData = bonsRes.data;
          const bons = bonsData.bons_travail || [];
          setContracts(bons);
          cacheBons(bons);
        }
      } catch (e) {
        setContracts(getCachedBons());
      }
      
      if (emps.length > 0) setSelectedEmployee(emps[0]);
    } catch (error) {
      console.error("Erreur chargement:", error);
      // En cas d'erreur réseau, basculer sur les caches
      const cachedEmps = getCachedEmployees();
      const cachedBons = getCachedBons();
      setEmployees(cachedEmps);
      setContracts(cachedBons);
      if (cachedEmps.length > 0) setSelectedEmployee(cachedEmps[0]);
    } finally {
      setLoading(false);
    }
  }, [isOnline]);

  // Charger le punch actuel
  const loadCurrentPunch = useCallback(async () => {
    if (!selectedEmployee) return;
    try {
      const response = await api.getCurrentPunch(selectedEmployee.id);
      setCurrentPunch(response.has_active_punch ? response.punch : null);
    } catch (error) {
      console.error("Erreur chargement punch actuel:", error);
    }
  }, [selectedEmployee]);

  // Charger l'historique
  const loadPunchHistory = useCallback(async () => {
    if (!selectedEmployee) return;
    try {
      const today = new Date();
      const thirtyDaysAgo = new Date(today.setDate(today.getDate() - 30)).toISOString().split('T')[0];
      const response = await api.getEmployeePunches(selectedEmployee.id, thirtyDaysAgo);
      setPunchHistory(response.punches || []);
    } catch (error) {
      console.error("Erreur chargement historique:", error);
    }
  }, [selectedEmployee]);

  // Synchroniser les pointages hors-ligne
  const syncOfflinePunches = useCallback(async () => {
    if (syncInProgress.current) return;
    const queue = getOfflineQueue();
    if (queue.length === 0) return;
    syncInProgress.current = true;
    setIsSyncing(true);
    let synced = 0;
    const failed = [];
    for (const item of queue) {
      try {
        const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
        if (item.action === 'punch_in') {
          const res = await axios.post(`${API_URL}/api/punch/in`, item.payload);
          if (res.status === 200) {
            const data = res.data;
            // Remplacer le punch local par l'ID réel du serveur
            if (data.punch?.id && currentPunch?.id === item.local_id) {
              setCurrentPunch(data.punch);
              saveOfflineCurrentPunch(data.punch);
            }
            synced++;
          } else { failed.push(item); }
        } else if (item.action === 'punch_out') {
          const res = await axios.post(`${API_URL}/api/punch/out`, item.payload);
          if (res.status === 200) { clearOfflineCurrentPunch(); synced++; }
          else { failed.push(item); }
        }
      } catch (e) { failed.push(item); }
    }
    saveOfflineQueue(failed);
    setPendingPunches(failed);
    setIsSyncing(false);
    syncInProgress.current = false;
    if (synced > 0) {
      toast.success(`${synced} pointage${synced > 1 ? 's' : ''} synchronisé${synced > 1 ? 's' : ''} avec succès`);
      loadPunchHistory();
    }
    if (failed.length > 0) {
      toast.error(`${failed.length} pointage${failed.length > 1 ? 's' : ''} n'ont pas pu être synchronisés`);
    }
  }, [currentPunch, loadPunchHistory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Détection connexion
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Connexion rétablie — synchronisation en cours...', { duration: 3000 });
      syncOfflinePunches();
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Mode hors-ligne activé — les pointages seront enregistrés localement', { duration: 4000 });
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline); };
  }, [syncOfflinePunches]);

  // Sync automatique des punches en attente au chargement
  useEffect(() => {
    if (isOnline && pendingPunches.length > 0) {
      syncOfflinePunches();
    }
  }, [isOnline, pendingPunches.length, syncOfflinePunches]);

  // Charger le punch hors-ligne en mémoire locale si hors-ligne
  useEffect(() => {
    if (!isOnline) {
      const offlinePunch = getOfflineCurrentPunch();
      if (offlinePunch) setCurrentPunch(offlinePunch);
    }
  }, [isOnline]);

  useEffect(() => {
    if (selectedEmployee) {
      loadCurrentPunch();
      loadPunchHistory();
    }
  }, [selectedEmployee, loadCurrentPunch, loadPunchHistory]);

  // Timer pour le punch en cours
  useEffect(() => {
    let interval;
    if (currentPunch && currentPunch.punch_in_time) {
      interval = setInterval(() => {
        const start = new Date(currentPunch.punch_in_time);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        setElapsedTime(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentPunch]);

  // Les fonctions loadData, syncOfflinePunches, loadCurrentPunch, loadPunchHistory
  // sont maintenant définies AVANT les useEffect avec useCallback

  const getLocation = useCallback(() => {
    setGettingLocation(true);
    setLocationError(null);
    
    if (!navigator.geolocation) {
      setLocationError("La géolocalisation n'est pas supportée par votre navigateur");
      setGettingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };

        // 🗺️ [v13.63 Phase 3 — 2026-02-28] Passage au backend central
        //   `/api/geo/reverse-geocode` (Google Maps + fallback Nominatim +
        //   cache 24h partagé). Fallback coord si tout échoue.
        const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
        try {
          const response = await axios.get(`${API_URL}/api/geo/reverse-geocode`, {
            params: { lat: coords.latitude, lng: coords.longitude },
          });
          coords.adresse = response.data?.adresse
            || `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`;
        } catch (e) {
          coords.adresse = `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`;
        }
        
        setLocation(coords);
        setGettingLocation(false);
      },
      (error) => {
        setLocationError(
          error.code === 1 ? "Permission refusée" :
          error.code === 2 ? "Position non disponible" :
          "Délai dépassé"
        );
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  const handlePunchInAdmin = async () => {
    if (!selectedEmployee) return;
    const punchPayload = {
      employee_id: selectedEmployee.id,
      contrat_id: 'admin_hours',
      type_punch: 'admin',
      employee_nom: `${selectedEmployee.prenom} ${selectedEmployee.nom}`,
    };
    setPunching(true);
    try {
      const response = await api.punchIn(punchPayload);
      setCurrentPunch(response.punch);
      toast.success('Journée commencée !');
      loadPunchHistory();
    } catch (error) {
      toast.error("Erreur lors du punch");
    } finally {
      setPunching(false);
    }
  };

  const handlePunchIn = async () => {
    if (!selectedEmployee || !selectedContract) {
      toast.error("Sélectionnez un contrat");
      return;
    }
    
    const bon = contracts.find(b => b.id === selectedContract);
    const punchPayload = {
      employee_id: selectedEmployee.id,
      contrat_id: selectedContract,
      latitude: location?.latitude,
      longitude: location?.longitude,
      adresse: location?.adresse,
    };

    setPunching(true);
    try {
      if (!isOnline) {
        // Mode hors-ligne : enregistrer localement
        const localId = `offline_${Date.now()}`;
        const localPunch = {
          id: localId,
          employee_id: selectedEmployee.id,
          employee_nom: `${selectedEmployee.prenom} ${selectedEmployee.nom}`,
          contrat_id: selectedContract,
          contrat_nom: bon?.dossier_nom || bon?.id || selectedContract,
          punch_in_time: new Date().toISOString(),
          statut: 'en_cours',
          is_offline: true,
        };
        setCurrentPunch(localPunch);
        saveOfflineCurrentPunch(localPunch);
        const queue = getOfflineQueue();
        queue.push({ action: 'punch_in', local_id: localId, payload: punchPayload, timestamp: new Date().toISOString() });
        saveOfflineQueue(queue);
        setPendingPunches(queue);
        setShowPunchInModal(false);
        toast.success("Punch IN enregistré localement (hors-ligne)");
        return;
      }

      const response = await api.punchIn(punchPayload);
      setCurrentPunch(response.punch);
      setShowPunchInModal(false);
      
      if (response.avertissement) {
        toast.warning(`Avertissement: ${response.avertissement.description}`);
      } else {
        toast.success("Punch IN enregistré !");
      }
      loadPunchHistory();
    } catch (error) {
      console.error("Erreur punch in:", error);
      if (!navigator.onLine) {
        // Basculer en mode hors-ligne si erreur réseau
        const localId = `offline_${Date.now()}`;
        const localPunch = {
          id: localId,
          employee_id: selectedEmployee.id,
          contrat_id: selectedContract,
          contrat_nom: bon?.id || selectedContract,
          punch_in_time: new Date().toISOString(),
          statut: 'en_cours',
          is_offline: true,
        };
        setCurrentPunch(localPunch);
        saveOfflineCurrentPunch(localPunch);
        const queue = getOfflineQueue();
        queue.push({ action: 'punch_in', local_id: localId, payload: punchPayload, timestamp: new Date().toISOString() });
        saveOfflineQueue(queue);
        setPendingPunches(queue);
        setShowPunchInModal(false);
        toast.success("Punch IN enregistré localement (hors-ligne)");
      } else {
        toast.error(error.response?.data?.detail || "Erreur lors du punch IN");
      }
    } finally {
      setPunching(false);
    }
  };

  const handlePauseStart = async () => {
    if (!currentPunch) return;
    try {
      const res = await api.post('/punch/pause/start', { punch_id: currentPunch.id });
      setCurrentPunch(prev => ({ ...prev, pause_start_time: res.data?.pause_start_time || new Date().toISOString() }));
      toast.success('Pause débutée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur début de pause');
    }
  };

  const handlePauseEnd = async () => {
    if (!currentPunch) return;
    try {
      const res = await api.post('/punch/pause/end', { punch_id: currentPunch.id });
      const heuresPause = res.data?.heures_pause || 0;
      setCurrentPunch(prev => ({ ...prev, pause_start_time: null, heures_pause: heuresPause }));
      toast.success(`Pause terminée — ${Math.round(heuresPause * 60)} min de pause enregistrées`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur fin de pause');
    }
  };

  const handlePunchOut = async () => {
    if (!currentPunch) {
      toast.error("Aucun punch en cours");
      return;
    }
    
    setPunching(true);
    try {
      if (!isOnline || currentPunch.is_offline) {
        // Mode hors-ligne : enregistrer localement
        const punchOutData = {
          punch_id: currentPunch.id,
          latitude: location?.latitude,
          longitude: location?.longitude,
          adresse: location?.adresse,
          heures_pause: selectedEmployee?.type_employe === 'admin' ? (currentPunch.heures_pause || 0) : 0,
          punch_out_time: new Date().toISOString(),
        };
        const queue = getOfflineQueue();
        queue.push({ action: 'punch_out', payload: punchOutData, timestamp: new Date().toISOString() });
        saveOfflineQueue(queue);
        setPendingPunches(queue);
        clearOfflineCurrentPunch();
        setCurrentPunch(null);
        setElapsedTime(null);
        setShowPunchOutModal(false);
        toast.success("Punch OUT enregistré localement (hors-ligne)");
        return;
      }

      const response = await api.punchOut({
        punch_id: currentPunch.id,
        latitude: location?.latitude,
        longitude: location?.longitude,
        adresse: location?.adresse,
        // Admin: utilise les heures_pause déjà calculées par les boutons pause; terrain: ignoré (backend force 30 min auto)
        heures_pause: selectedEmployee?.type_employe === 'admin' ? (currentPunch.heures_pause || 0) : 0
      });
      
      setCurrentPunch(null);
      setElapsedTime(null);
      setShowPunchOutModal(false);
      toast.success(`Punch OUT enregistré ! ${response.punch?.heures_nettes?.toFixed(2)} heures travaillées`);
      loadPunchHistory();
    } catch (error) {
      console.error("Erreur punch out:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du punch OUT");
    } finally {
      setPunching(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  // Stats du mois
  const monthStats = {
    totalHeures: punchHistory.reduce((acc, p) => acc + (p.heures_nettes || 0), 0),
    totalJours: new Set(punchHistory.map(p => p.punch_in_time?.split('T')[0])).size,
    retards: punchHistory.filter(p => p.alerte_retard).length,
    oublis: punchHistory.filter(p => p.alerte_oubli_depunch).length
  };

  return (
    <div className="space-y-6" data-testid="pointage-page">
      {/* Bannière mode hors-ligne */}
      {!isOnline && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-800">
            <WifiOff className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-semibold text-sm">Mode hors-ligne actif</p>
              <p className="text-xs text-amber-600">Les pointages sont enregistrés localement et seront synchronisés à la reconnexion.</p>
            </div>
          </div>
          {pendingPunches.length > 0 && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 shrink-0">
              {pendingPunches.length} en attente
            </Badge>
          )}
        </div>
      )}

      {/* Bannière synchronisation */}
      {isOnline && pendingPunches.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-blue-800">
            {isSyncing ? <RefreshCw className="h-5 w-5 animate-spin text-blue-600" /> : <CloudUpload className="h-5 w-5 text-blue-600" />}
            <div>
              <p className="font-semibold text-sm">{isSyncing ? 'Synchronisation en cours...' : 'Pointages hors-ligne en attente'}</p>
              <p className="text-xs text-blue-600">{pendingPunches.length} pointage{pendingPunches.length > 1 ? 's' : ''} à synchroniser</p>
            </div>
          </div>
          {!isSyncing && (
            <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100" onClick={syncOfflinePunches}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Synchroniser
            </Button>
          )}
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Clock className="h-7 w-7 text-blue-600" />
            Système de Pointage
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-slate-500">Punch IN/OUT avec géolocalisation</p>
            <Badge className={isOnline ? 'bg-green-100 text-green-700 border-green-200' : 'bg-amber-100 text-amber-700 border-amber-200'}>
              {isOnline ? <><Wifi className="h-3 w-3 mr-1" />En ligne</> : <><WifiOff className="h-3 w-3 mr-1" />Hors-ligne</>}
            </Badge>
          </div>
        </div>
        
        {/* Sélection employé (pour test/admin) */}
        <Select 
          value={selectedEmployee?.id || ""} 
          onValueChange={(v) => setSelectedEmployee(employees.find(e => e.id === v))}
        >
          <SelectTrigger className="w-full sm:w-[250px]">
            <User className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Sélectionner un employé" />
          </SelectTrigger>
          <SelectContent>
            {employees.map(emp => (
              <SelectItem key={emp.id} value={emp.id}>
                {emp.prenom} {emp.nom}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedEmployee ? (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
          {/* Colonne principale - Punch */}
          <div className="xl:col-span-2 space-y-4 sm:space-y-6">
            {/* Status Card */}
            <Card className={`${currentPunch ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-br from-slate-50 to-blue-50'}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <EmployeeAvatar photoUrl={selectedEmployee.photo_url} prenom={selectedEmployee.prenom} nom={selectedEmployee.nom} size="md" />
                    <div>
                      <h2 className="text-lg sm:text-xl font-semibold text-slate-800">
                        {selectedEmployee.prenom} {selectedEmployee.nom}
                      </h2>
                      <p className="text-slate-500 text-sm">{selectedEmployee.departement} - {selectedEmployee.type_employe}</p>
                    </div>
                  </div>
                  
                  {currentPunch ? (
                    <div className="text-right">
                      <Badge className="bg-green-500 text-white mb-2">En cours{currentPunch?.is_offline ? ' (hors-ligne)' : ''}</Badge>
                      <p className="text-2xl sm:text-3xl font-mono font-bold text-green-700">{elapsedTime || "00:00:00"}</p>
                      <p className="text-sm text-slate-500">Depuis {formatTime(currentPunch.punch_in_time)}</p>
                    </div>
                  ) : (
                    <Badge className="bg-slate-200 text-slate-600">Pas de punch actif</Badge>
                  )}
                </div>
                
                {/* Punch Buttons */}
                <div className="flex gap-3 mt-5">
                  {!currentPunch ? (
                    <Button 
                      size="lg"
                      className="flex-1 bg-green-600 hover:bg-green-700 h-14 sm:h-16 text-base sm:text-lg"
                      onClick={() => {
                        getLocation();
                        // Admin employees punch in directly without contract selection
                        if (selectedEmployee?.type_employe === 'admin') {
                          setSelectedContract('admin_hours');
                          handlePunchInAdmin();
                        } else {
                          setShowPunchInModal(true);
                        }
                      }}
                      data-testid="punch-in-btn"
                    >
                      <Play className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
                      {selectedEmployee?.type_employe === 'admin' ? 'Commencer la journée' : 'PUNCH IN'}
                    </Button>
                  ) : (
                    <Button 
                      size="lg"
                      className="flex-1 bg-red-600 hover:bg-red-700 h-14 sm:h-16 text-base sm:text-lg"
                      onClick={() => {
                        getLocation();
                        setShowPunchOutModal(true);
                      }}
                      data-testid="punch-out-btn"
                    >
                      <Square className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
                      {selectedEmployee?.type_employe === 'admin' ? 'Fin de journée' : 'PUNCH OUT'}
                    </Button>
                  )}
                </div>

                {/* Boutons de pause pour les employés ADMIN uniquement */}
                {currentPunch && selectedEmployee?.type_employe === 'admin' && (
                  <div className="flex gap-3 mt-2">
                    {currentPunch.pause_start_time ? (
                      <Button size="sm" variant="outline" className="flex-1 border-amber-400 text-amber-700 hover:bg-amber-50" onClick={handlePauseEnd} data-testid="btn-terminer-pause">
                        ☕ Pause terminée
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-50" onClick={handlePauseStart} data-testid="btn-debuter-pause">
                        ☕ Débuter ma pause
                      </Button>
                    )}
                    {currentPunch.heures_pause > 0 && (
                      <span className="self-center text-xs text-slate-500">
                        {Math.round((currentPunch.heures_pause || 0) * 60)} min de pause enregistrées
                      </span>
                    )}
                  </div>
                )}

                {/* Current punch details */}
                {currentPunch && (
                  <div className="mt-6 p-4 bg-white rounded-lg border border-green-200">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      Contrat: {currentPunch.contrat_nom || 'Chantier'}
                    </h3>
                    {currentPunch.punch_in_adresse && (
                      <p className="text-sm text-slate-500 flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {currentPunch.punch_in_adresse}
                      </p>
                    )}
                    {currentPunch.alerte_retard && (
                      <Badge className="mt-2 bg-orange-100 text-orange-700">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Retard de {currentPunch.minutes_retard} min
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  Géolocalisation
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Info sur la permission de localisation */}
                <div className="mb-3 bg-blue-50 border border-blue-100 rounded-lg p-2.5 text-xs text-blue-700">
                  <p className="font-medium">Pourquoi la localisation est demandée ?</p>
                  <p className="mt-0.5 text-blue-600">Votre position est enregistrée uniquement lors du pointage d'entrée et de sortie pour confirmer votre présence sur le chantier. Elle n'est jamais tracée en temps réel.</p>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    {gettingLocation ? (
                      <p className="text-slate-500 flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Récupération de la position...
                      </p>
                    ) : location ? (
                      <div>
                        <p className="font-medium text-green-700 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          Position obtenue
                        </p>
                        <p className="text-sm text-slate-500 mt-1">{location.adresse}</p>
                        <p className="text-xs text-slate-400">
                          Lat: {location.latitude?.toFixed(6)}, Lng: {location.longitude?.toFixed(6)}
                          {location.accuracy && ` (±${Math.round(location.accuracy)}m)`}
                        </p>
                      </div>
                    ) : locationError ? (
                      <p className="text-red-600 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        {locationError}
                      </p>
                    ) : (
                      <p className="text-slate-500">Cliquez pour obtenir votre position</p>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={getLocation}
                    disabled={gettingLocation}
                  >
                    <Navigation className="h-4 w-4 mr-2" />
                    {location ? "Actualiser" : "Localiser"}
                  </Button>
                </div>
                
                {/* Mini Map Placeholder */}
                {location && (
                  <div className="mt-4 h-48 bg-slate-100 rounded-lg flex items-center justify-center border">
                    <a 
                      href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-2"
                    >
                      <MapPin className="h-5 w-5" />
                      Voir sur Google Maps
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Colonne latérale - Stats & Historique */}
          <div className="space-y-6">
            {/* Stats du mois */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Ce mois</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-slate-600">Heures travaillées</span>
                  <span className="text-xl font-bold text-blue-700">{monthStats.totalHeures.toFixed(2).replace('.', ',')}h</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-slate-600">Jours travaillés</span>
                  <span className="text-xl font-bold text-green-700">{monthStats.totalJours}</span>
                </div>
                {monthStats.retards > 0 && (
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <span className="text-slate-600">Retards</span>
                    <Badge className="bg-orange-100 text-orange-700">{monthStats.retards}</Badge>
                  </div>
                )}
                {monthStats.oublis > 0 && (
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span className="text-slate-600">Oublis dépunch</span>
                    <Badge className="bg-red-100 text-red-700">{monthStats.oublis}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Historique récent */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Historique récent
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {punchHistory.slice(0, 10).map((punch) => (
                    <div 
                      key={punch.id} 
                      className={`p-3 rounded-lg border ${
                        punch.statut === 'en_cours' ? 'bg-green-50 border-green-200' :
                        punch.statut === 'valide' ? 'bg-blue-50 border-blue-200' :
                        punch.statut === 'refuse' ? 'bg-red-50 border-red-200' :
                        'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{formatDate(punch.punch_in_time)}</p>
                          <p className="text-xs text-slate-500">{punch.contrat_nom || 'Chantier'}</p>
                        </div>
                        <Badge className={
                          punch.statut === 'en_cours' ? 'bg-green-100 text-green-700' :
                          punch.statut === 'valide' ? 'bg-blue-100 text-blue-700' :
                          punch.statut === 'refuse' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {punch.statut === 'en_cours' ? 'En cours' :
                           punch.statut === 'valide' ? 'Validé' :
                           punch.statut === 'refuse' ? 'Refusé' : 'Complété'}
                        </Badge>
                      </div>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span className="text-green-600">IN: {formatTime(punch.punch_in_time)}</span>
                        {punch.punch_out_time && (
                          <span className="text-red-600">OUT: {formatTime(punch.punch_out_time)}</span>
                        )}
                        {punch.heures_nettes && (
                          <span className="text-slate-600 font-medium">{punch.heures_nettes.toFixed(2).replace('.', ',')}h</span>
                        )}
                      </div>
                      {(punch.alerte_retard || punch.alerte_oubli_depunch) && (
                        <div className="flex gap-2 mt-2">
                          {punch.alerte_retard && (
                            <Badge className="bg-orange-100 text-orange-700 text-xs">Retard</Badge>
                          )}
                          {punch.alerte_oubli_depunch && (
                            <Badge className="bg-red-100 text-red-700 text-xs">Oubli dépunch</Badge>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {punchHistory.length === 0 && (
                    <p className="text-center text-slate-500 py-4">Aucun historique</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <User className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">Sélectionnez un employé pour commencer</p>
          </CardContent>
        </Card>
      )}

      {/* Modal Punch IN */}
      <Dialog open={showPunchInModal} onOpenChange={setShowPunchInModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-green-600" />
              Punch IN
            </DialogTitle>
            <DialogDescription>
              Sélectionnez le contrat sur lequel vous travaillez
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contrat</Label>
              <Select value={selectedContract || ""} onValueChange={setSelectedContract}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un contrat" />
                </SelectTrigger>
                <SelectContent>
                  {contracts.length > 0 ? contracts.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.dossier_nom || c.client_nom || c.id?.slice(0, 12)}
                    </SelectItem>
                  )) : (
                    <SelectItem value="default">Contrat par défaut</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {location && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Position: {location.adresse?.substring(0, 50)}...
                </p>
              </div>
            )}
            
            {!location && !gettingLocation && (
              <Button variant="outline" className="w-full" onClick={getLocation}>
                <Navigation className="h-4 w-4 mr-2" />
                Obtenir ma position
              </Button>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPunchInModal(false)}>
              Annuler
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handlePunchIn}
              disabled={punching || !selectedContract}
            >
              {punching ? "..." : "Confirmer Punch IN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Punch OUT */}
      <Dialog open={showPunchOutModal} onOpenChange={setShowPunchOutModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Square className="h-5 w-5 text-red-600" />
              Punch OUT
            </DialogTitle>
            <DialogDescription>
              Confirmez la fin de votre journée
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {currentPunch && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{currentPunch.contrat_nom || 'Chantier'}</p>
                <p className="text-sm text-slate-500">Début: {formatTime(currentPunch.punch_in_time)}</p>
                <p className="text-lg font-mono font-bold text-blue-600 mt-2">{elapsedTime}</p>
              </div>
            )}
            
            <div className="space-y-2">
              {selectedEmployee?.type_employe === 'admin' ? (
                <>
                  {/* Admin: afficher les heures de pause déjà suivies */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                    <p className="text-sm text-indigo-700 font-medium">Pause suivie par le système</p>
                    {currentPunch?.heures_pause > 0 ? (
                      <p className="text-sm text-indigo-600">{Math.round((currentPunch.heures_pause || 0) * 60)} min de pause enregistrées</p>
                    ) : (
                      <p className="text-xs text-indigo-500">Aucune pause enregistrée. Utilisez les boutons "Débuter ma pause" pour suivre votre pause.</p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded" data-testid="pause-auto-info">
                  Pause de 30 min retirée automatiquement pour les employés terrain.
                </p>
              )}
            </div>
            
            {location && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Position: {location.adresse?.substring(0, 50)}...
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPunchOutModal(false)}>
              Annuler
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={handlePunchOut}
              disabled={punching}
            >
              {punching ? "..." : "Confirmer Punch OUT"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// RAPPORT DE POINTAGE — moteur de recherche des heures validées
// ═══════════════════════════════════════════════════════════════

